import React, { useState, useEffect, useMemo } from 'react';
import { 
  Plane, Building2, Bus, FileText, ArrowRight, Star, Clock, 
  Luggage, Users, Calendar, MapPin, Check, X, ChevronDown, ChevronUp,
  RefreshCw, Filter, ArrowUpDown, SlidersHorizontal, ArrowLeft, ShoppingCart, Layers, ToggleLeft, ToggleRight
} from 'lucide-react';
import { Button } from './ui/button';
import { useCurrency } from '../context/CurrencyContext';
import { searchHotels, searchFlights, getTransportOptions, getVisaOptions, getCombinedFlights } from '../services/umrahApi';
import BookingSummary from './BookingSummary';
import CombinedFlightCard from './CombinedFlightCard';

const SearchResults = ({ 
  searchParams, 
  onSelectPackage, 
  onBack 
}) => {
  const { formatPrice, currency } = useCurrency();
  const [activeTab, setActiveTab] = useState('flights');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBookingSummary, setShowBookingSummary] = useState(false);
  
  // Flight selection step: 'outbound' | 'inbound'
  const [flightStep, setFlightStep] = useState('outbound');
  
  // Results state
  const [outboundFlights, setOutboundFlights] = useState([]);
  const [inboundFlights, setInboundFlights] = useState([]);
  const [combinedFlights, setCombinedFlights] = useState([]);  // BFM style combined flights
  const [makkahHotels, setMakkahHotels] = useState([]);
  const [madinahHotels, setMadinahHotels] = useState([]);
  const [transportOptions, setTransportOptions] = useState([]);
  const [visaOptions, setVisaOptions] = useState([]);
  
  // Selected items
  const [selectedOutboundFlight, setSelectedOutboundFlight] = useState(null);
  const [selectedInboundFlight, setSelectedInboundFlight] = useState(null);
  const [selectedCombinedFlight, setSelectedCombinedFlight] = useState(null);  // Combined flight selection
  const [selectedMakkahHotel, setSelectedMakkahHotel] = useState(null);
  const [selectedMakkahRoom, setSelectedMakkahRoom] = useState(null);
  const [selectedMadinahHotel, setSelectedMadinahHotel] = useState(null);
  const [selectedMadinahRoom, setSelectedMadinahRoom] = useState(null);
  const [selectedTransport, setSelectedTransport] = useState(null);
  const [selectedVisa, setSelectedVisa] = useState(null);
  
  // Transport & Ziyarat state
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedTransfers, setSelectedTransfers] = useState(['airport-makkah', 'makkah-madinah', 'madinah-airport']);
  const [selectedZiyarat, setSelectedZiyarat] = useState([]);
  
  // Flight view mode: 'step' (outbound then inbound) or 'combined' (BFM style)
  const [flightViewMode, setFlightViewMode] = useState('combined');
  
  // Flight filters
  const [flightFilters, setFlightFilters] = useState({
    stops: 'all',
    priceMin: '',
    priceMax: '',
    baggage: 'all',
    departTime: 'all'
  });
  
  // Hotel filters - Updated format matching reference design
  const [hotelFilters, setHotelFilters] = useState({
    stars: [],  // Array of selected star ratings [5, 4, 3]
    budget: [],  // Array of selected budget ranges ['50-100', '100-150', etc.]
    distance: [], // Array of selected distance ranges ['0-500m', '500m-1km', etc.]
    freeWifi: false,
    freeCancellation: false,
    breakfastIncluded: false,
    swimmingPool: false,
    parking: false,
    gym: false
  });
  
  // Hotel sorting
  const [hotelSortBy, setHotelSortBy] = useState('recommended');
  
  // Sorting
  const [sortBy, setSortBy] = useState('cheapest');
  const [priceDisplay, setPriceDisplay] = useState('perPerson');
  
  // Mobile filter modal state
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showMobilePackage, setShowMobilePackage] = useState(false);

  // Initialize visa selection from searchParams
  useEffect(() => {
    if (searchParams?.needVisa && searchParams?.visaCost > 0) {
      // Build visa name from selected types
      const visaNames = [];
      const passports = searchParams.visaPassports || {};
      const types = searchParams.visaTypes || {};
      
      if (passports.british > 0 && types.british) {
        const typeLabel = types.british === 'single' ? 'Single Entry' : types.british === 'multiple' ? 'Multiple Entry' : 'Umrah Visa';
        visaNames.push(`🇬🇧 ${passports.british}× ${typeLabel}`);
      }
      if (passports.eu > 0 && types.eu) {
        const typeLabel = types.eu === 'multiple' ? 'Multiple Entry' : 'Umrah Visa';
        visaNames.push(`🇪🇺 ${passports.eu}× ${typeLabel}`);
      }
      if (passports.other > 0) {
        visaNames.push(`🌍 ${passports.other}× Umrah Visa`);
      }
      
      setSelectedVisa({
        id: 'from-builder',
        name: visaNames.join(' + ') || 'Selected Visa',
        price: searchParams.visaCost,
        fromBuilder: true
      });
    }
  }, [searchParams]);

  // Auto-select vehicle based on passenger count
  useEffect(() => {
    if (transportOptions.length > 0 && !selectedVehicle) {
      const passengers = (searchParams.adults || 0) + (searchParams.children || 0) + (searchParams.infants || 0);
      const recommendedVehicle = getRecommendedVehicle(passengers, transportOptions);
      if (recommendedVehicle) {
        setSelectedVehicle(recommendedVehicle);
        setSelectedTransport(recommendedVehicle);
      }
    }
  }, [transportOptions, searchParams.adults, searchParams.children, searchParams.infants]);

  // Helper function to get recommended vehicle based on passenger count
  const getRecommendedVehicle = (passengers, vehicles) => {
    // Sort by capacity to find the best fit
    const sortedVehicles = [...vehicles].sort((a, b) => a.capacity - b.capacity);
    
    // Find the smallest vehicle that can accommodate all passengers
    for (const vehicle of sortedVehicles) {
      if (passengers >= (vehicle.min_passengers || 1) && passengers <= (vehicle.max_passengers || vehicle.capacity)) {
        return vehicle;
      }
    }
    
    // If no perfect match, find the smallest vehicle that fits
    for (const vehicle of sortedVehicles) {
      if (passengers <= (vehicle.max_passengers || vehicle.capacity)) {
        return vehicle;
      }
    }
    
    // Return largest vehicle if all else fails
    return sortedVehicles[sortedVehicles.length - 1];
  };

  useEffect(() => {
    loadSearchResults();
  }, [searchParams]);

  const loadSearchResults = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const totalPassengers = searchParams.adults + searchParams.children + (searchParams.infants || 0);
      
      // Fetch combined flights (BFM style) along with other data
      const [
        outboundResults,
        inboundResults,
        combinedResults,
        makkahResults,
        madinahResults,
        transport,
        visa
      ] = await Promise.all([
        // Outbound flights
        searchFlights({
          origin: searchParams.departureAirport?.code || 'LHR',
          destination: searchParams.arrivalAirport || 'JED',
          departureDate: searchParams.departureDate,
          adults: searchParams.adults || 2,
          children: searchParams.children || 0,
          infants: searchParams.infants || 0,
        }),
        // Inbound flights
        searchFlights({
          origin: searchParams.returnFrom || 'MED',
          destination: searchParams.departureAirport?.code || 'LHR',
          departureDate: searchParams.returnDate,
          adults: searchParams.adults || 2,
          children: searchParams.children || 0,
          infants: searchParams.infants || 0,
        }),
        // Combined flights (BFM style)
        getCombinedFlights({
          origin: searchParams.departureAirport?.code || 'LHR',
          destination: searchParams.arrivalAirport || 'JED',
          returnFrom: searchParams.returnFrom || 'MED',
          departureDate: searchParams.departureDate,
          returnDate: searchParams.returnDate,
          adults: searchParams.adults || 2,
          children: searchParams.children || 0,
          infants: searchParams.infants || 0,
        }),
        // Hotels
        searchHotels({
          city: 'makkah',
          checkIn: searchParams.departureDate,
          checkOut: calculateCheckOut(searchParams.departureDate, searchParams.makkahNights),
          guests: totalPassengers,
        }),
        searchHotels({
          city: 'madinah',
          checkIn: calculateCheckOut(searchParams.departureDate, searchParams.makkahNights),
          checkOut: searchParams.returnDate,
          guests: totalPassengers,
        }),
        getTransportOptions(totalPassengers),
        getVisaOptions(),
      ]);
      
      setOutboundFlights(outboundResults || []);
      setInboundFlights(inboundResults || []);
      setCombinedFlights(combinedResults?.combined_options || []);
      setMakkahHotels(makkahResults || []);
      setMadinahHotels(madinahResults || []);
      setTransportOptions(transport || []);
      setVisaOptions(visa || []);
      
      // Auto-select cheapest combined flight
      if (combinedResults?.cheapest_overall) {
        setSelectedCombinedFlight(combinedResults.cheapest_overall);
      }
      
    } catch (err) {
      console.error('Search error:', err);
      setError('Failed to load search results. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const calculateCheckOut = (checkIn, nights) => {
    const date = new Date(checkIn);
    date.setDate(date.getDate() + (nights || 5));
    return date.toISOString().split('T')[0];
  };

  const calculateTotalPrice = () => {
    let total = 0;
    const passengers = searchParams.adults + searchParams.children + (searchParams.infants || 0);
    
    // Use combined flight price if in combined mode, otherwise individual flights
    if (flightViewMode === 'combined' && selectedCombinedFlight) {
      total += selectedCombinedFlight.pricing.combined_total;
    } else {
      if (selectedOutboundFlight) {
        total += selectedOutboundFlight.pricing.total_fare;
      }
      if (selectedInboundFlight) {
        total += selectedInboundFlight.pricing.total_fare;
      }
    }
    
    if (selectedMakkahRoom) {
      total += selectedMakkahRoom.total_price;
    } else if (selectedMakkahHotel) {
      total += selectedMakkahHotel.total_price * passengers;
    }
    if (selectedMadinahRoom) {
      total += selectedMadinahRoom.total_price;
    } else if (selectedMadinahHotel) {
      total += selectedMadinahHotel.total_price * passengers;
    }
    
    // Transport - use route-based pricing
    if (selectedVehicle && selectedVehicle.route_prices) {
      // Add prices for selected transfer routes
      selectedTransfers.forEach(routeId => {
        total += selectedVehicle.route_prices[routeId] || 0;
      });
      // Add prices for selected Ziyarat tours
      selectedZiyarat.forEach(tourId => {
        total += selectedVehicle.route_prices[tourId] || 0;
      });
    } else if (selectedTransport) {
      // Fallback to old pricing if route_prices not available
      total += selectedTransport.standard_package_price || selectedTransport.price || 0;
    }
    
    if (selectedVisa) {
      total += selectedVisa.price * passengers;
    }
    
    return total;
  };

  const getFilteredFlights = (flights) => {
    let filtered = [...flights];
    
    if (flightFilters.stops === 'direct') {
      filtered = filtered.filter(f => f.legs[0]?.stops === 0);
    } else if (flightFilters.stops === '1stop') {
      filtered = filtered.filter(f => f.legs[0]?.stops === 1);
    }
    
    if (sortBy === 'cheapest') {
      filtered.sort((a, b) => a.pricing.total_fare - b.pricing.total_fare);
    } else if (sortBy === 'fastest') {
      filtered.sort((a, b) => {
        const durationA = parseInt(a.legs[0]?.duration) || 999;
        const durationB = parseInt(b.legs[0]?.duration) || 999;
        return durationA - durationB;
      });
    }
    
    return filtered;
  };

  // Helper function to get distance in meters from hotel
  const getDistanceInMeters = (hotel) => {
    const distStr = hotel.distance_to_haram || '999km';
    const dist = parseFloat(distStr.replace(/[^\d.]/g, ''));
    // Convert to meters
    if (distStr.toLowerCase().includes('km')) {
      return dist * 1000;
    }
    return dist; // Already in meters
  };

  // Helper to count hotels per filter option
  const getFilterCounts = (hotels) => {
    const counts = {
      stars: { 5: 0, 4: 0, 3: 0, 2: 0 },
      budget: { '50-100': 0, '100-150': 0, '150-200': 0, '200-250': 0, '250+': 0 },
      distance: { '0-500m': 0, '500m-1km': 0, '1km-2km': 0, '2km+': 0 }
    };
    
    hotels.forEach(h => {
      // Star counts
      const stars = h.stars || 0;
      if (stars >= 5) counts.stars[5]++;
      if (stars >= 4) counts.stars[4]++;
      if (stars >= 3) counts.stars[3]++;
      if (stars >= 2) counts.stars[2]++;
      
      // Budget counts
      const price = h.price_per_night || 0;
      if (price >= 50 && price < 100) counts.budget['50-100']++;
      else if (price >= 100 && price < 150) counts.budget['100-150']++;
      else if (price >= 150 && price < 200) counts.budget['150-200']++;
      else if (price >= 200 && price < 250) counts.budget['200-250']++;
      else if (price >= 250) counts.budget['250+']++;
      
      // Distance counts
      const dist = getDistanceInMeters(h);
      if (dist < 500) counts.distance['0-500m']++;
      else if (dist < 1000) counts.distance['500m-1km']++;
      else if (dist < 2000) counts.distance['1km-2km']++;
      else counts.distance['2km+']++;
    });
    
    return counts;
  };

  // Hotel filtering function - Updated for new filter format
  const getFilteredHotels = (hotels) => {
    let filtered = [...hotels];
    
    // Star rating filter (multiple selection)
    if (hotelFilters.stars.length > 0) {
      filtered = filtered.filter(h => hotelFilters.stars.includes(h.stars || 0));
    }
    
    // Budget filter (multiple selection)
    if (hotelFilters.budget.length > 0) {
      filtered = filtered.filter(h => {
        const price = h.price_per_night || 0;
        return hotelFilters.budget.some(range => {
          if (range === '50-100') return price >= 50 && price < 100;
          if (range === '100-150') return price >= 100 && price < 150;
          if (range === '150-200') return price >= 150 && price < 200;
          if (range === '200-250') return price >= 200 && price < 250;
          if (range === '250+') return price >= 250;
          return false;
        });
      });
    }
    
    // Distance filter (multiple selection)
    if (hotelFilters.distance.length > 0) {
      filtered = filtered.filter(h => {
        const dist = getDistanceInMeters(h);
        return hotelFilters.distance.some(range => {
          if (range === '0-500m') return dist < 500;
          if (range === '500m-1km') return dist >= 500 && dist < 1000;
          if (range === '1km-2km') return dist >= 1000 && dist < 2000;
          if (range === '2km+') return dist >= 2000;
          return false;
        });
      });
    }
    
    // Amenity filters
    if (hotelFilters.freeWifi) {
      filtered = filtered.filter(h => h.amenities?.includes('Free WiFi') || h.amenities?.includes('WiFi'));
    }
    if (hotelFilters.freeCancellation) {
      filtered = filtered.filter(h => h.refundable);
    }
    if (hotelFilters.breakfastIncluded) {
      filtered = filtered.filter(h => h.breakfast_included);
    }
    if (hotelFilters.swimmingPool) {
      filtered = filtered.filter(h => h.amenities?.includes('Swimming Pool') || h.amenities?.includes('Pool'));
    }
    if (hotelFilters.parking) {
      filtered = filtered.filter(h => h.amenities?.includes('Parking') || h.amenities?.includes('Free Parking'));
    }
    if (hotelFilters.gym) {
      filtered = filtered.filter(h => h.amenities?.includes('Gym') || h.amenities?.includes('Fitness Center'));
    }
    
    // Sort
    if (hotelSortBy === 'price_low') {
      filtered.sort((a, b) => (a.price_per_night || 0) - (b.price_per_night || 0));
    } else if (hotelSortBy === 'price_high') {
      filtered.sort((a, b) => (b.price_per_night || 0) - (a.price_per_night || 0));
    } else if (hotelSortBy === 'stars') {
      filtered.sort((a, b) => (b.stars || 0) - (a.stars || 0));
    } else if (hotelSortBy === 'distance') {
      filtered.sort((a, b) => getDistanceInMeters(a) - getDistanceInMeters(b));
    }
    
    return filtered;
  };

  const formatTime = (dateTimeStr) => {
    if (!dateTimeStr) return '--:--';
    const time = dateTimeStr.split('T')[1];
    return time ? time.slice(0, 5) : '--:--';
  };

  const formatDate = (dateTimeStr) => {
    if (!dateTimeStr) return '';
    const date = new Date(dateTimeStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const handleOutboundSelect = (flight) => {
    setSelectedOutboundFlight(flight);
    setFlightStep('inbound');
  };

  const handleInboundSelect = (flight) => {
    setSelectedInboundFlight(flight);
    setActiveTab('makkah');
  };

  const handleBackToOutbound = () => {
    setFlightStep('outbound');
    setSelectedOutboundFlight(null);
  };

  const tabs = [
    { 
      id: 'flights', 
      label: flightViewMode === 'combined' 
        ? 'Round Trip Flights' 
        : (flightStep === 'outbound' ? 'Outbound Flights' : 'Return Flights'), 
      icon: Plane, 
      count: flightViewMode === 'combined' ? combinedFlights.length : (flightStep === 'outbound' ? outboundFlights.length : inboundFlights.length) 
    },
    { id: 'makkah', label: 'Makkah Hotels', icon: Building2, count: makkahHotels.length },
    { id: 'madinah', label: 'Madinah Hotels', icon: Building2, count: madinahHotels.length },
    { id: 'transport', label: 'Transport', icon: Bus, count: transportOptions.length },
    { id: 'visa', label: 'Visa', icon: FileText, count: visaOptions.length },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-green-500 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800">Searching for the best packages...</h2>
          <p className="text-gray-500 mt-2">This may take a moment</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-xl shadow-lg max-w-md">
          <X className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Search Failed</h2>
          <p className="text-gray-500 mb-4">{error}</p>
          <Button onClick={loadSearchResults} className="bg-green-500 hover:bg-green-600">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  const currentFlights = flightStep === 'outbound' ? outboundFlights : inboundFlights;
  const filteredFlights = getFilteredFlights(currentFlights);
  const directFlights = filteredFlights.filter(f => f.legs[0]?.stops === 0);
  const connectingFlights = filteredFlights.filter(f => f.legs[0]?.stops > 0);

  // Check if package is complete enough for booking
  const canProceedToBooking = flightViewMode === 'combined' 
    ? selectedCombinedFlight 
    : (selectedOutboundFlight && selectedInboundFlight);

  // Handle combined flight selection
  const handleSelectCombinedFlight = (flight) => {
    setSelectedCombinedFlight(flight);
    setActiveTab('makkah');  // Move to hotel selection
  };

  // Handle proceed to booking summary
  const handleProceedToBooking = () => {
    setShowBookingSummary(true);
  };

  // Handle booking confirmation
  const handleConfirmBooking = (bookingData) => {
    console.log('Booking confirmed:', bookingData);
    // Here you would call the backend API to create the booking
    alert('Booking submitted successfully! You will receive a confirmation email shortly.');
    onSelectPackage && onSelectPackage(bookingData);
  };

  // Show Booking Summary page
  if (showBookingSummary) {
    return (
      <BookingSummary
        packageData={{
          outboundFlight: selectedOutboundFlight,
          inboundFlight: selectedInboundFlight,
          makkahHotel: selectedMakkahHotel,
          makkahRoom: selectedMakkahRoom,
          madinahHotel: selectedMadinahHotel,
          madinahRoom: selectedMadinahRoom,
          transport: selectedVehicle || selectedTransport,
          selectedTransfers,
          selectedZiyarat,
          visa: selectedVisa
        }}
        searchParams={searchParams}
        onBack={() => setShowBookingSummary(false)}
        onConfirmBooking={handleConfirmBooking}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 pb-20 lg:pb-0">
      {/* Header */}
      <div className="bg-white border-b shadow-sm py-2 md:py-4 px-2 md:px-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto">
          {/* Mobile Header */}
          <div className="flex items-center justify-between lg:hidden mb-2">
            <button onClick={onBack} className="flex items-center gap-1 text-gray-600 hover:text-gray-800 text-sm">
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </button>
            <div className="text-right">
              <div className="text-xs text-gray-500">Package Total</div>
              <div className="text-lg font-bold text-green-600">{formatPrice(calculateTotalPrice())}</div>
            </div>
          </div>
          
          {/* Mobile Tabs - Horizontal Scroll */}
          <div className="flex gap-1.5 overflow-x-auto pb-1 lg:hidden scrollbar-hide">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  if (tab.id === 'flights') {
                    setActiveTab(tab.id);
                  } else if (selectedOutboundFlight && selectedInboundFlight) {
                    setActiveTab(tab.id);
                  }
                }}
                disabled={tab.id !== 'flights' && (!selectedOutboundFlight || !selectedInboundFlight)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full font-medium text-xs whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-[#d4af37] text-white'
                    : tab.id !== 'flights' && (!selectedOutboundFlight || !selectedInboundFlight)
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-100 text-gray-600'
                }`}
              >
                <tab.icon className="w-3.5 h-3.5" />
                <span>{tab.label.replace(' Hotels', '').replace(' Flights', '')}</span>
                <span className={`text-xs px-1 py-0.5 rounded ${
                  activeTab === tab.id ? 'bg-white/20' : 'bg-gray-200'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
          
          {/* Desktop Header */}
          <div className="hidden lg:flex items-center justify-between">
            <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-800">
              <ArrowRight className="w-5 h-5 rotate-180" />
              <span>Modify Search</span>
            </button>
            
            {/* Desktop Tabs */}
            <div className="flex gap-2">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => {
                    if (tab.id === 'flights') {
                      setActiveTab(tab.id);
                    } else if (selectedOutboundFlight && selectedInboundFlight) {
                      setActiveTab(tab.id);
                    }
                  }}
                  disabled={tab.id !== 'flights' && (!selectedOutboundFlight || !selectedInboundFlight)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                    activeTab === tab.id
                      ? 'bg-[#d4af37] text-white'
                      : tab.id !== 'flights' && (!selectedOutboundFlight || !selectedInboundFlight)
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    activeTab === tab.id ? 'bg-white/20' : 'bg-gray-200'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
            
            <div className="text-right">
              <div className="text-sm text-gray-500">Package Total</div>
              <div className="text-xl font-bold text-green-600">{formatPrice(calculateTotalPrice())}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-2 md:px-4 py-3 md:py-6">
        <div className="flex gap-4 lg:gap-6">
          {/* Left Sidebar - Filters (Hidden on mobile) */}
          {activeTab === 'flights' && (
            <div className="hidden lg:block w-56 flex-shrink-0">
              {/* Flight Step Indicator */}
              <div className={`${flightStep === 'outbound' ? 'bg-blue-500' : 'bg-green-500'} text-white px-4 py-3 rounded-t-lg font-medium`}>
                <div className="flex items-center gap-2">
                  <Plane className={`w-4 h-4 ${flightStep === 'inbound' ? 'rotate-180' : ''}`} />
                  {flightStep === 'outbound' ? 'Select Outbound' : 'Select Return'}
                </div>
                <div className="text-xs opacity-80 mt-1">
                  {flightStep === 'outbound' 
                    ? `${searchParams.departureAirport?.code || 'LON'} → ${searchParams.arrivalAirport || 'JED'}`
                    : `${searchParams.arrivalAirport || 'JED'} → ${searchParams.departureAirport?.code || 'LON'}`
                  }
                </div>
              </div>
              
              {/* Back to Outbound Button */}
              {flightStep === 'inbound' && (
                <button
                  onClick={handleBackToOutbound}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 text-sm flex items-center gap-2 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Change Outbound Flight
                </button>
              )}
              
              <div className="bg-white rounded-b-lg shadow-sm p-4 space-y-6">
                {/* Results Count */}
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <Filter className="w-4 h-4" />
                  {filteredFlights.length} Results
                </div>
                
                {/* Stops Filter */}
                <div>
                  <h4 className="font-semibold text-gray-700 mb-3 text-sm uppercase">Stops</h4>
                  <div className="space-y-2">
                    {[
                      { value: 'all', label: 'All' },
                      { value: 'direct', label: 'Direct' },
                      { value: '1stop', label: '1 Connection' },
                    ].map(option => (
                      <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                        <input 
                          type="radio" 
                          name="stops" 
                          checked={flightFilters.stops === option.value}
                          onChange={() => setFlightFilters(prev => ({ ...prev, stops: option.value }))}
                          className="text-blue-500"
                        />
                        <span className="text-sm text-gray-600">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Price Range */}
                <div>
                  <h4 className="font-semibold text-gray-700 mb-3 text-sm uppercase">Price Range</h4>
                  <div className="flex gap-2">
                    <input 
                      type="number" 
                      placeholder="Min" 
                      className="w-full px-2 py-1.5 border rounded text-sm"
                      value={flightFilters.priceMin}
                      onChange={(e) => setFlightFilters(prev => ({ ...prev, priceMin: e.target.value }))}
                    />
                    <span className="text-gray-400">-</span>
                    <input 
                      type="number" 
                      placeholder="Max" 
                      className="w-full px-2 py-1.5 border rounded text-sm"
                      value={flightFilters.priceMax}
                      onChange={(e) => setFlightFilters(prev => ({ ...prev, priceMax: e.target.value }))}
                    />
                  </div>
                </div>
                
                {/* Departure Time */}
                <div>
                  <h4 className="font-semibold text-gray-700 mb-3 text-sm uppercase">Departure Time</h4>
                  <div className="space-y-2">
                    {[
                      { value: 'all', label: 'All Times' },
                      { value: 'morning', label: 'Morning (05:00 - 11:59)' },
                      { value: 'afternoon', label: 'Afternoon (12:00 - 17:59)' },
                      { value: 'evening', label: 'Evening (18:00 - 23:59)' },
                    ].map(option => (
                      <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                        <input 
                          type="radio" 
                          name="departTime" 
                          checked={flightFilters.departTime === option.value}
                          onChange={() => setFlightFilters(prev => ({ ...prev, departTime: option.value }))}
                          className="text-blue-500"
                        />
                        <span className="text-sm text-gray-600">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Hotel Filters Sidebar - Shows when on hotel tabs */}
          {(activeTab === 'makkah' || activeTab === 'madinah') && (
            <div className="hidden md:block w-64 flex-shrink-0">
              {/* Hotel Tab Indicator */}
              <div className="bg-[#1e3a5f] text-white px-4 py-3 rounded-t-lg font-medium">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  {activeTab === 'makkah' ? 'Makkah Hotels' : 'Madinah Hotels'}
                </div>
                <div className="text-xs opacity-80 mt-1">
                  {activeTab === 'makkah' 
                    ? `${searchParams.makkahNights || 5} nights`
                    : `${searchParams.madinahNights || 3} nights`
                  }
                </div>
              </div>
              
              <div className="bg-white rounded-b-lg shadow-sm p-4 space-y-6">
                {/* Sorting */}
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3 text-sm">Sorting</h4>
                  <select 
                    value={hotelSortBy}
                    onChange={(e) => setHotelSortBy(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a5f] focus:border-[#1e3a5f]"
                  >
                    <option value="recommended">Recommended</option>
                    <option value="price_low">Price: Low to High</option>
                    <option value="price_high">Price: High to Low</option>
                    <option value="stars">Star Rating</option>
                    <option value="distance">Distance to Haram</option>
                  </select>
                </div>

                {/* Star Filter */}
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3 text-sm">Star Filter</h4>
                  <div className="space-y-2">
                    {(() => {
                      const hotels = activeTab === 'makkah' ? makkahHotels : madinahHotels;
                      const counts = getFilterCounts(hotels);
                      return [5, 4, 3, 2].map(stars => (
                        <label key={stars} className="flex items-center justify-between cursor-pointer group">
                          <div className="flex items-center gap-2">
                            <input 
                              type="checkbox"
                              checked={hotelFilters.stars.includes(stars)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setHotelFilters(prev => ({ ...prev, stars: [...prev.stars, stars] }));
                                } else {
                                  setHotelFilters(prev => ({ ...prev, stars: prev.stars.filter(s => s !== stars) }));
                                }
                              }}
                              className="w-4 h-4 rounded border-gray-300 text-[#d4af37] focus:ring-[#d4af37]"
                            />
                            <div className="flex">
                              {[...Array(stars)].map((_, i) => (
                                <Star key={i} className="w-4 h-4 fill-[#d4af37] text-[#d4af37]" />
                              ))}
                              {[...Array(5 - stars)].map((_, i) => (
                                <Star key={i} className="w-4 h-4 text-gray-300" />
                              ))}
                            </div>
                          </div>
                          <span className="text-xs text-gray-500">({counts.stars[stars]})</span>
                        </label>
                      ));
                    })()}
                  </div>
                </div>

                {/* Budget Filter */}
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3 text-sm">Budget</h4>
                  <div className="space-y-2">
                    {(() => {
                      const hotels = activeTab === 'makkah' ? makkahHotels : madinahHotels;
                      const counts = getFilterCounts(hotels);
                      const budgetOptions = [
                        { value: '50-100', label: '£50 - £100' },
                        { value: '100-150', label: '£100 - £150' },
                        { value: '150-200', label: '£150 - £200' },
                        { value: '200-250', label: '£200 - £250' },
                        { value: '250+', label: '£250+' },
                      ];
                      return budgetOptions.map(option => (
                        <label key={option.value} className="flex items-center justify-between cursor-pointer group">
                          <div className="flex items-center gap-2">
                            <input 
                              type="checkbox"
                              checked={hotelFilters.budget.includes(option.value)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setHotelFilters(prev => ({ ...prev, budget: [...prev.budget, option.value] }));
                                } else {
                                  setHotelFilters(prev => ({ ...prev, budget: prev.budget.filter(b => b !== option.value) }));
                                }
                              }}
                              className="w-4 h-4 rounded border-gray-300 text-[#d4af37] focus:ring-[#d4af37]"
                            />
                            <span className="text-sm text-gray-700 group-hover:text-gray-900">{option.label}</span>
                          </div>
                          <span className="text-xs text-gray-500">({counts.budget[option.value]})</span>
                        </label>
                      ));
                    })()}
                  </div>
                </div>

                {/* Distance from Haram */}
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3 text-sm">Distance from Haram</h4>
                  <div className="space-y-2">
                    {(() => {
                      const hotels = activeTab === 'makkah' ? makkahHotels : madinahHotels;
                      const counts = getFilterCounts(hotels);
                      const distanceOptions = [
                        { value: '0-500m', label: 'Less than 500m' },
                        { value: '500m-1km', label: '500m - 1km' },
                        { value: '1km-2km', label: '1km - 2km' },
                        { value: '2km+', label: 'More than 2km' },
                      ];
                      return distanceOptions.map(option => (
                        <label key={option.value} className="flex items-center justify-between cursor-pointer group">
                          <div className="flex items-center gap-2">
                            <input 
                              type="checkbox"
                              checked={hotelFilters.distance.includes(option.value)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setHotelFilters(prev => ({ ...prev, distance: [...prev.distance, option.value] }));
                                } else {
                                  setHotelFilters(prev => ({ ...prev, distance: prev.distance.filter(d => d !== option.value) }));
                                }
                              }}
                              className="w-4 h-4 rounded border-gray-300 text-[#d4af37] focus:ring-[#d4af37]"
                            />
                            <span className="text-sm text-gray-700 group-hover:text-gray-900">{option.label}</span>
                          </div>
                          <span className="text-xs text-gray-500">({counts.distance[option.value]})</span>
                        </label>
                      ));
                    })()}
                  </div>
                </div>

                {/* Amenities */}
                <div>
                  <h4 className="font-semibold text-gray-800 mb-3 text-sm">Amenities</h4>
                  <div className="space-y-2">
                    {[
                      { key: 'freeWifi', label: 'Free WiFi' },
                      { key: 'freeCancellation', label: 'Free Cancellation' },
                      { key: 'breakfastIncluded', label: 'Breakfast Included' },
                      { key: 'swimmingPool', label: 'Swimming Pool' },
                      { key: 'parking', label: 'Parking' },
                      { key: 'gym', label: 'Gym' },
                    ].map(amenity => (
                      <label key={amenity.key} className="flex items-center gap-2 cursor-pointer group">
                        <input 
                          type="checkbox"
                          checked={hotelFilters[amenity.key]}
                          onChange={(e) => setHotelFilters(prev => ({ ...prev, [amenity.key]: e.target.checked }))}
                          className="w-4 h-4 rounded border-gray-300 text-[#d4af37] focus:ring-[#d4af37]"
                        />
                        <span className="text-sm text-gray-700 group-hover:text-gray-900">{amenity.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Clear Filters */}
                <button 
                  onClick={() => setHotelFilters({
                    stars: [],
                    budget: [],
                    distance: [],
                    freeWifi: false,
                    freeCancellation: false,
                    breakfastIncluded: false,
                    swimmingPool: false,
                    parking: false,
                    gym: false
                  })}
                  className="w-full py-2 text-sm text-[#1e3a5f] hover:text-[#d4af37] border border-[#1e3a5f] hover:border-[#d4af37] rounded-lg transition-colors"
                >
                  Clear All Filters
                </button>

                {/* Results Count */}
                <div className="pt-2 border-t text-center">
                  <span className="text-sm font-medium text-gray-700">
                    {getFilteredHotels(activeTab === 'makkah' ? makkahHotels : madinahHotels).length} hotels found
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="flex-1">
            {/* Flight Results */}
            {activeTab === 'flights' && (
              <div>
                {/* Flight View Mode Toggle */}
                <div className="bg-white rounded-lg shadow-sm p-4 mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="font-semibold text-gray-700">View Mode:</span>
                    <div className="flex bg-gray-100 rounded-lg p-1">
                      <button
                        onClick={() => setFlightViewMode('combined')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          flightViewMode === 'combined' 
                            ? 'bg-green-500 text-white' 
                            : 'text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <Layers className="w-4 h-4 inline mr-1" />
                        Round Trip (BFM)
                      </button>
                      <button
                        onClick={() => setFlightViewMode('step')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          flightViewMode === 'step' 
                            ? 'bg-blue-500 text-white' 
                            : 'text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <ArrowRight className="w-4 h-4 inline mr-1" />
                        Step by Step
                      </button>
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">
                    {flightViewMode === 'combined' 
                      ? `${combinedFlights.length} combined options` 
                      : `${filteredFlights.length} ${flightStep} flights`}
                  </div>
                </div>
                
                {/* Combined Flights View (BFM Style) */}
                {flightViewMode === 'combined' && (
                  <div className="space-y-4">
                    {combinedFlights.length === 0 ? (
                      <div className="bg-white rounded-lg p-8 text-center">
                        <Plane className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">No combined flight options available</p>
                      </div>
                    ) : (
                      combinedFlights.map((flight, index) => (
                        <div 
                          key={index}
                          className={`bg-white rounded-xl shadow-sm border-2 transition-all cursor-pointer hover:shadow-md ${
                            selectedCombinedFlight?.pricing?.combined_total === flight.pricing.combined_total
                              ? 'border-green-500 ring-2 ring-green-500/20'
                              : 'border-transparent hover:border-gray-200'
                          }`}
                          onClick={() => handleSelectCombinedFlight(flight)}
                        >
                          {/* Badge */}
                          {(flight.is_cheapest || flight.type === 'combinable') && (
                            <div className={`px-4 py-2 flex items-center justify-between ${
                              flight.is_cheapest ? 'bg-green-500' : flight.type === 'combinable' ? 'bg-blue-500' : 'bg-gray-500'
                            }`}>
                              <div className="flex items-center gap-2">
                                {flight.type === 'combinable' && <Check className="w-4 h-4 text-white" />}
                                <Plane className="w-4 h-4 text-white" />
                                <span className="text-white font-semibold text-sm">
                                  {flight.badge || (flight.type === 'combinable' ? 'Combinable Fare' : 'Best Mix')}
                                </span>
                                {flight.type === 'combinable' && (
                                  <span className="text-white/80 text-xs ml-2">• {flight.airline_name}</span>
                                )}
                              </div>
                              {flight.pricing.discount > 0 && (
                                <span className="bg-white/20 text-white text-xs px-2 py-1 rounded">
                                  Save {formatPrice(flight.pricing.discount)}
                                </span>
                              )}
                            </div>
                          )}
                          
                          {/* Flight Details */}
                          <div className="p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {/* Outbound */}
                              <div className="bg-gray-50 rounded-lg p-4">
                                <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                                  <Plane className="w-4 h-4" />
                                  <span>Outbound Flight</span>
                                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                    {searchParams.departureDate}
                                  </span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                                    {flight.type === 'combinable' ? flight.airline : flight.outbound_airline}
                                  </div>
                                  <div className="flex-1">
                                    <div className="font-semibold text-gray-800">
                                      {flight.outbound.departure} → {flight.outbound.arrival}
                                    </div>
                                    <div className="text-sm text-gray-500 flex items-center gap-2">
                                      <span>{flight.outbound.duration}</span>
                                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                                        flight.outbound.stops === 0 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                                      }`}>
                                        {flight.outbound.stops === 0 ? 'Direct' : `${flight.outbound.stops} Stop`}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <div className="font-semibold text-gray-700">{formatPrice(flight.outbound.fare)}</div>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Return */}
                              <div className="bg-gray-50 rounded-lg p-4">
                                <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                                  <Plane className="w-4 h-4 rotate-180" />
                                  <span>Return Flight</span>
                                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                                    {searchParams.returnDate}
                                  </span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                                    {flight.type === 'combinable' ? flight.airline : flight.return_airline}
                                  </div>
                                  <div className="flex-1">
                                    <div className="font-semibold text-gray-800">
                                      {flight.return.departure} → {flight.return.arrival}
                                    </div>
                                    <div className="text-sm text-gray-500 flex items-center gap-2">
                                      <span>{flight.return.duration}</span>
                                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                                        flight.return.stops === 0 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                                      }`}>
                                        {flight.return.stops === 0 ? 'Direct' : `${flight.return.stops} Stop`}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <div className="font-semibold text-gray-700">{formatPrice(flight.return.fare)}</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Total Price */}
                            <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className="flex items-center gap-1 text-sm text-gray-500">
                                  <Luggage className="w-4 h-4" />
                                  <span>2x23kg</span>
                                </div>
                                {flight.type === 'combinable' && (
                                  <span className="text-xs text-green-600 font-medium">5% combinable discount</span>
                                )}
                              </div>
                              <div className="text-right">
                                <div className="text-2xl font-bold text-gray-900">
                                  {formatPrice(flight.pricing.combined_total)}
                                </div>
                                <div className="text-xs text-gray-500">Round trip total</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
                
                {/* Step-by-Step Flight View */}
                {flightViewMode === 'step' && (
                  <>
                {/* Selected Outbound Summary (when selecting inbound) */}
                {flightStep === 'inbound' && selectedOutboundFlight && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                          <Plane className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-blue-800">Outbound Flight Selected</div>
                          <div className="text-xs text-blue-600">
                            {selectedOutboundFlight.legs[0]?.segments[0]?.airline_name} • 
                            {selectedOutboundFlight.legs[0]?.departure} → {selectedOutboundFlight.legs[0]?.arrival} • 
                            {formatTime(selectedOutboundFlight.legs[0]?.departure_time)}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-blue-800">
                          {formatPrice(selectedOutboundFlight.pricing.total_fare)}
                        </div>
                        <button 
                          onClick={handleBackToOutbound}
                          className="text-xs text-blue-600 hover:underline"
                        >
                          Change
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Sort Header */}
                <div className="bg-white rounded-lg shadow-sm p-4 mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xl font-bold text-gray-800">{filteredFlights.length}</span>
                    <span className="text-gray-600">
                      {flightStep === 'outbound' ? 'Outbound' : 'Return'} Flights Available
                    </span>
                    {directFlights.length > 0 && (
                      <span className="text-green-600 text-sm">(+ {directFlights.length} Direct)</span>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="flex bg-gray-100 rounded-lg p-1">
                      {[
                        { value: 'best', label: 'Best' },
                        { value: 'cheapest', label: 'Cheapest' },
                        { value: 'fastest', label: 'Fastest' }
                      ].map(option => (
                        <button
                          key={option.value}
                          onClick={() => setSortBy(option.value)}
                          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                            sortBy === option.value
                              ? 'bg-gray-800 text-white'
                              : 'text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    
                    <div className="flex bg-gray-100 rounded-lg p-1">
                      <button
                        onClick={() => setPriceDisplay('perPerson')}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                          priceDisplay === 'perPerson' ? 'bg-gray-800 text-white' : 'text-gray-600'
                        }`}
                      >
                        Per Person
                      </button>
                      <button
                        onClick={() => setPriceDisplay('total')}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                          priceDisplay === 'total' ? 'bg-gray-800 text-white' : 'text-gray-600'
                        }`}
                      >
                        Total Price
                      </button>
                    </div>
                  </div>
                </div>

                {/* Direct Flights Section */}
                {directFlights.length > 0 && (
                  <div className="mb-6">
                    <div className="bg-green-500 text-white px-4 py-2 rounded-t-lg flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Star className="w-4 h-4 fill-white" />
                        <span className="font-semibold">Direct Flights</span>
                      </div>
                      <span className="bg-green-600 px-2 py-0.5 rounded text-xs">Best Price</span>
                    </div>
                    <div className="space-y-3 mt-3">
                      {directFlights.map(flight => (
                        <FlightCard 
                          key={flight.id} 
                          flight={flight} 
                          selected={flightStep === 'outbound' 
                            ? selectedOutboundFlight?.id === flight.id 
                            : selectedInboundFlight?.id === flight.id}
                          onSelect={() => flightStep === 'outbound' 
                            ? handleOutboundSelect(flight) 
                            : handleInboundSelect(flight)}
                          formatPrice={formatPrice}
                          formatTime={formatTime}
                          formatDate={formatDate}
                          priceDisplay={priceDisplay}
                          passengers={searchParams.adults + searchParams.children + (searchParams.infants || 0)}
                          isDirect={true}
                          flightType={flightStep}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Connecting Flights */}
                {connectingFlights.length > 0 && (
                  <div className="space-y-3">
                    {connectingFlights.map(flight => (
                      <FlightCard 
                        key={flight.id} 
                        flight={flight} 
                        selected={flightStep === 'outbound' 
                          ? selectedOutboundFlight?.id === flight.id 
                          : selectedInboundFlight?.id === flight.id}
                        onSelect={() => flightStep === 'outbound' 
                          ? handleOutboundSelect(flight) 
                          : handleInboundSelect(flight)}
                        formatPrice={formatPrice}
                        formatTime={formatTime}
                        formatDate={formatDate}
                        priceDisplay={priceDisplay}
                        passengers={searchParams.adults + searchParams.children + (searchParams.infants || 0)}
                        isDirect={false}
                        flightType={flightStep}
                      />
                    ))}
                  </div>
                )}

                <div className="mt-4 text-center text-sm text-gray-500">
                  1 - {filteredFlights.length} of {filteredFlights.length} flights
                </div>
                  </>
                )}
              </div>
            )}

            {/* Hotel Results (Makkah & Madinah) */}
            {(activeTab === 'makkah' || activeTab === 'madinah') && (
              <div>
                {/* Mobile Sort and Filter Bar */}
                <div className="md:hidden flex items-center justify-between bg-white rounded-lg p-3 mb-4 shadow-sm">
                  <span className="text-sm font-medium text-gray-700">
                    {getFilteredHotels(activeTab === 'makkah' ? makkahHotels : madinahHotels).length} hotels
                  </span>
                  <div className="flex gap-2">
                    <select 
                      value={hotelSortBy}
                      onChange={(e) => setHotelSortBy(e.target.value)}
                      className="px-3 py-1.5 border rounded-lg text-sm"
                    >
                      <option value="recommended">Recommended</option>
                      <option value="price_low">Price ↑</option>
                      <option value="price_high">Price ↓</option>
                      <option value="stars">Stars</option>
                      <option value="distance">Distance</option>
                    </select>
                    <button 
                      onClick={() => setShowMobileFilters(true)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-blue-500 text-white rounded-lg text-sm"
                    >
                      <Filter className="w-4 h-4" />
                      Filters
                    </button>
                  </div>
                </div>

                <HotelSection
                  hotels={getFilteredHotels(activeTab === 'makkah' ? makkahHotels : madinahHotels)}
                  selectedHotel={activeTab === 'makkah' ? selectedMakkahHotel : selectedMadinahHotel}
                  selectedRoom={activeTab === 'makkah' ? selectedMakkahRoom : selectedMadinahRoom}
                  onSelectHotel={(hotel) => activeTab === 'makkah' 
                    ? setSelectedMakkahHotel(hotel) 
                    : setSelectedMadinahHotel(hotel)
                  }
                  onSelectRoom={(room) => {
                    if (activeTab === 'makkah') {
                      setSelectedMakkahRoom(room);
                      setTimeout(() => setActiveTab('madinah'), 300);
                    } else {
                      setSelectedMadinahRoom(room);
                      setTimeout(() => setActiveTab('transport'), 300);
                    }
                  }}
                  onBackToHotels={() => activeTab === 'makkah'
                    ? (setSelectedMakkahHotel(null), setSelectedMakkahRoom(null))
                    : (setSelectedMadinahHotel(null), setSelectedMadinahRoom(null))
                  }
                  formatPrice={formatPrice}
                  hotelType={activeTab}
                  nights={activeTab === 'makkah' ? searchParams.makkahNights : searchParams.madinahNights}
                  guests={searchParams.adults + searchParams.children + (searchParams.infants || 0)}
                />
              </div>
            )}

            {/* Transport & Ziyarat Options */}
            {activeTab === 'transport' && (
              <TransportZiyaratSection
                transportOptions={transportOptions}
                selectedVehicle={selectedVehicle}
                setSelectedVehicle={(vehicle) => {
                  setSelectedVehicle(vehicle);
                  setSelectedTransport(vehicle);
                }}
                selectedTransfers={selectedTransfers}
                setSelectedTransfers={setSelectedTransfers}
                selectedZiyarat={selectedZiyarat}
                setSelectedZiyarat={setSelectedZiyarat}
                passengers={(searchParams.adults || 0) + (searchParams.children || 0) + (searchParams.infants || 0)}
                formatPrice={formatPrice}
                getRecommendedVehicle={getRecommendedVehicle}
                onBack={onBack}
              />
            )}

            {/* Visa Options */}
            {activeTab === 'visa' && (
              <div className="space-y-6">
                {/* British Passport Visa Options */}
                <div className="bg-white rounded-xl shadow-sm p-4">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl">🇬🇧</span>
                    <div>
                      <h3 className="font-bold text-gray-800">British Passport Holders</h3>
                      <p className="text-sm text-gray-500">Choose your visa type</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {[
                      { id: 'british-single', name: 'Single Entry Visa', price: 50, description: 'Valid for one entry', processing: '3-5 business days', icon: '🎫' },
                      { id: 'british-multiple', name: 'Multiple Entry Visa', price: 120, description: 'Valid for multiple entries', processing: '5-7 business days', icon: '🎟️' },
                      { id: 'british-umrah', name: 'Umrah Visa', price: 250, description: '30-day Umrah pilgrimage visa', processing: '3-5 business days', icon: '🕋' },
                    ].map(visa => (
                      <div
                        key={visa.id}
                        onClick={() => setSelectedVisa(visa)}
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          selectedVisa?.id === visa.id
                            ? 'border-green-500 bg-green-50 ring-2 ring-green-200'
                            : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                        }`}
                      >
                        <div className="text-2xl mb-2">{visa.icon}</div>
                        <h4 className="font-semibold text-gray-800">{visa.name}</h4>
                        <p className="text-xs text-gray-500 mt-1">{visa.description}</p>
                        <div className="mt-3 flex items-center justify-between">
                          <span className="text-xl font-bold text-blue-600">{formatPrice(visa.price)}</span>
                          <span className="text-xs text-gray-400">per person</span>
                        </div>
                        <p className="text-xs text-green-600 mt-2">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {visa.processing}
                        </p>
                        {selectedVisa?.id === visa.id && (
                          <div className="mt-2 flex items-center gap-1 text-green-600 text-sm font-medium">
                            <Check className="w-4 h-4" /> Selected
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* EU Passport Visa Options */}
                <div className="bg-white rounded-xl shadow-sm p-4">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl">🇪🇺</span>
                    <div>
                      <h3 className="font-bold text-gray-800">EU Passport Holders</h3>
                      <p className="text-sm text-gray-500">Choose your visa type</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      { id: 'eu-multiple', name: 'Multiple Entry Visa', price: 120, description: 'Valid for multiple entries', processing: '5-7 business days', icon: '🎟️' },
                      { id: 'eu-umrah', name: 'Umrah Visa', price: 250, description: '30-day Umrah pilgrimage visa', processing: '3-5 business days', icon: '🕋' },
                    ].map(visa => (
                      <div
                        key={visa.id}
                        onClick={() => setSelectedVisa(visa)}
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          selectedVisa?.id === visa.id
                            ? 'border-green-500 bg-green-50 ring-2 ring-green-200'
                            : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                        }`}
                      >
                        <div className="text-2xl mb-2">{visa.icon}</div>
                        <h4 className="font-semibold text-gray-800">{visa.name}</h4>
                        <p className="text-xs text-gray-500 mt-1">{visa.description}</p>
                        <div className="mt-3 flex items-center justify-between">
                          <span className="text-xl font-bold text-blue-600">{formatPrice(visa.price)}</span>
                          <span className="text-xs text-gray-400">per person</span>
                        </div>
                        <p className="text-xs text-green-600 mt-2">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {visa.processing}
                        </p>
                        {selectedVisa?.id === visa.id && (
                          <div className="mt-2 flex items-center gap-1 text-green-600 text-sm font-medium">
                            <Check className="w-4 h-4" /> Selected
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-red-500 mt-3">⚠️ Single-Entry Visa not available for EU passport holders</p>
                </div>

                {/* Other Nationality Visa Options */}
                <div className="bg-white rounded-xl shadow-sm p-4">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl">🌍</span>
                    <div>
                      <h3 className="font-bold text-gray-800">Other Nationalities</h3>
                      <p className="text-sm text-gray-500">Available visa option</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-1 gap-3 max-w-md">
                    {[
                      { id: 'other-umrah', name: 'Umrah Visa', price: 250, description: '30-day Umrah pilgrimage visa', processing: '3-5 business days', icon: '🕋' },
                    ].map(visa => (
                      <div
                        key={visa.id}
                        onClick={() => setSelectedVisa(visa)}
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          selectedVisa?.id === visa.id
                            ? 'border-green-500 bg-green-50 ring-2 ring-green-200'
                            : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                        }`}
                      >
                        <div className="text-2xl mb-2">{visa.icon}</div>
                        <h4 className="font-semibold text-gray-800">{visa.name}</h4>
                        <p className="text-xs text-gray-500 mt-1">{visa.description}</p>
                        <div className="mt-3 flex items-center justify-between">
                          <span className="text-xl font-bold text-blue-600">{formatPrice(visa.price)}</span>
                          <span className="text-xs text-gray-400">per person</span>
                        </div>
                        <p className="text-xs text-green-600 mt-2">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {visa.processing}
                        </p>
                        {selectedVisa?.id === visa.id && (
                          <div className="mt-2 flex items-center gap-1 text-green-600 text-sm font-medium">
                            <Check className="w-4 h-4" /> Selected
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* No Visa Needed Option */}
                <div 
                  onClick={() => setSelectedVisa(null)}
                  className={`bg-white rounded-xl shadow-sm p-4 cursor-pointer transition-all border-2 ${
                    selectedVisa === null ? 'border-gray-400 bg-gray-50' : 'border-transparent hover:border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                      <X className="w-6 h-6 text-gray-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">I don&apos;t need a visa</h3>
                      <p className="text-sm text-gray-500">I already have a valid visa or will arrange separately</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Sidebar - Package Summary (Hidden on mobile) */}
          <div className="hidden lg:block w-72 flex-shrink-0">
            <div className="bg-white rounded-xl shadow-lg p-4 sticky top-24">
              <h3 className="font-bold text-lg text-gray-800 mb-4">Your Package</h3>
              
              <div className="space-y-3">
                {/* Outbound Flight */}
                <div className={`p-3 rounded-lg ${selectedOutboundFlight ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Plane className={`w-4 h-4 ${selectedOutboundFlight ? 'text-green-600' : 'text-gray-400'}`} />
                      <span className="font-medium text-sm">Outbound</span>
                    </div>
                    {selectedOutboundFlight ? (
                      <span className="text-sm font-semibold text-green-600">
                        {formatPrice(selectedOutboundFlight.pricing.total_fare)}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">Not selected</span>
                    )}
                  </div>
                  {selectedOutboundFlight && (
                    <p className="text-xs text-gray-500 mt-1">
                      {selectedOutboundFlight.validating_carrier} - {selectedOutboundFlight.legs[0]?.departure} → {selectedOutboundFlight.legs[0]?.arrival}
                    </p>
                  )}
                </div>
                
                {/* Inbound Flight */}
                <div className={`p-3 rounded-lg ${selectedInboundFlight ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Plane className={`w-4 h-4 rotate-180 ${selectedInboundFlight ? 'text-green-600' : 'text-gray-400'}`} />
                      <span className="font-medium text-sm">Return</span>
                    </div>
                    {selectedInboundFlight ? (
                      <span className="text-sm font-semibold text-green-600">
                        {formatPrice(selectedInboundFlight.pricing.total_fare)}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">Not selected</span>
                    )}
                  </div>
                  {selectedInboundFlight && (
                    <p className="text-xs text-gray-500 mt-1">
                      {selectedInboundFlight.validating_carrier} - {selectedInboundFlight.legs[0]?.departure} → {selectedInboundFlight.legs[0]?.arrival}
                    </p>
                  )}
                </div>
                
                {/* Makkah Hotel */}
                <div className={`p-3 rounded-lg ${selectedMakkahRoom ? 'bg-green-50 border border-green-200' : selectedMakkahHotel ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Building2 className={`w-4 h-4 ${selectedMakkahRoom ? 'text-green-600' : selectedMakkahHotel ? 'text-amber-600' : 'text-gray-400'}`} />
                      <span className="font-medium text-sm">Makkah Hotel</span>
                    </div>
                    {selectedMakkahRoom ? (
                      <span className="text-sm font-semibold text-green-600">
                        {formatPrice(selectedMakkahRoom.total_price)}
                      </span>
                    ) : selectedMakkahHotel ? (
                      <span className="text-xs text-amber-600">Select room</span>
                    ) : (
                      <span className="text-xs text-gray-400">Not selected</span>
                    )}
                  </div>
                  {selectedMakkahRoom ? (
                    <p className="text-xs text-gray-500 mt-1">{selectedMakkahHotel?.name} • {selectedMakkahRoom.name}</p>
                  ) : selectedMakkahHotel && (
                    <p className="text-xs text-amber-600 mt-1">{selectedMakkahHotel.name}</p>
                  )}
                </div>
                
                {/* Madinah Hotel */}
                <div className={`p-3 rounded-lg ${selectedMadinahRoom ? 'bg-green-50 border border-green-200' : selectedMadinahHotel ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Building2 className={`w-4 h-4 ${selectedMadinahRoom ? 'text-green-600' : selectedMadinahHotel ? 'text-amber-600' : 'text-gray-400'}`} />
                      <span className="font-medium text-sm">Madinah Hotel</span>
                    </div>
                    {selectedMadinahRoom ? (
                      <span className="text-sm font-semibold text-green-600">
                        {formatPrice(selectedMadinahRoom.total_price)}
                      </span>
                    ) : selectedMadinahHotel ? (
                      <span className="text-xs text-amber-600">Select room</span>
                    ) : (
                      <span className="text-xs text-gray-400">Not selected</span>
                    )}
                  </div>
                  {selectedMadinahRoom ? (
                    <p className="text-xs text-gray-500 mt-1">{selectedMadinahHotel?.name} • {selectedMadinahRoom.name}</p>
                  ) : selectedMadinahHotel && (
                    <p className="text-xs text-amber-600 mt-1">{selectedMadinahHotel.name}</p>
                  )}
                </div>
                
                {/* Transport */}
                <div className={`p-3 rounded-lg ${selectedTransport ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Bus className={`w-4 h-4 ${selectedTransport ? 'text-green-600' : 'text-gray-400'}`} />
                      <span className="font-medium text-sm">Transport</span>
                    </div>
                    {selectedTransport ? (
                      <span className="text-sm font-semibold text-green-600">
                        {formatPrice(selectedTransport.standard_package_price || selectedTransport.price || 0)}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">Optional</span>
                    )}
                  </div>
                </div>
                
                {/* Visa */}
                <div className={`p-3 rounded-lg ${selectedVisa ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className={`w-4 h-4 ${selectedVisa ? 'text-green-600' : 'text-gray-400'}`} />
                      <span className="font-medium text-sm">Visa</span>
                    </div>
                    {selectedVisa ? (
                      <span className="text-sm font-semibold text-green-600">
                        {formatPrice(selectedVisa.price)}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">Optional</span>
                    )}
                  </div>
                  {selectedVisa && (
                    <p className="text-xs text-gray-500 mt-1">{selectedVisa.name}</p>
                  )}
                </div>
              </div>
              
              {/* Total */}
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-semibold">{formatPrice(calculateTotalPrice())}</span>
                </div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-600">Service Fee (5%)</span>
                  <span className="font-semibold">{formatPrice(calculateTotalPrice() * 0.05)}</span>
                </div>
                <div className="flex items-center justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="text-green-600">{formatPrice(calculateTotalPrice() * 1.05)}</span>
                </div>
                <div className="text-xs text-gray-500 mt-1 text-right">
                  Deposit: {formatPrice(50 * (searchParams.adults + searchParams.children + (searchParams.infants || 0)))} (£50/person)
                </div>
              </div>
              
              <Button
                onClick={handleProceedToBooking}
                disabled={!canProceedToBooking}
                className="w-full mt-4 h-12 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold rounded-xl disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <ShoppingCart className="w-5 h-5" />
                Continue to Booking
              </Button>
              
              {!canProceedToBooking && (
                <p className="text-xs text-gray-500 text-center mt-2">
                  Select flights to continue
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile Bottom Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 px-4 pb-4 pt-2">
        {/* Large centered Filters button - only shown on flights tab */}
        {activeTab === 'flights' && (
          <button
            onClick={() => setShowMobileFilters(true)}
            className="w-full flex items-center justify-center gap-3 py-4 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 rounded-full text-lg font-semibold text-white shadow-lg shadow-orange-500/30 transition-all"
          >
            <Filter className="w-5 h-5" />
            <span>Filters</span>
          </button>
        )}
        
        {/* View Package button for other tabs */}
        {activeTab !== 'flights' && (
          <button
            onClick={() => setShowMobilePackage(true)}
            className="w-full flex items-center justify-center gap-3 py-4 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 rounded-full text-lg font-semibold text-white shadow-lg shadow-green-500/30 transition-all"
          >
            <span>View Package</span>
            <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
              {formatPrice(calculateTotalPrice() * 1.05)}
            </span>
          </button>
        )}
      </div>
      
      {/* Mobile Filter Modal */}
      {showMobileFilters && (
        <div className="lg:hidden fixed inset-0 z-50">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowMobileFilters(false)}
          />
          
          {/* Modal Content */}
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[80vh] overflow-y-auto animate-slide-up">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
              <h3 className="font-bold text-lg">Filters</h3>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Flight Step Indicator */}
            <div className={`${flightStep === 'outbound' ? 'bg-blue-500' : 'bg-green-500'} text-white px-4 py-3 mx-4 mt-4 rounded-lg font-medium`}>
              <div className="flex items-center gap-2">
                <Plane className={`w-4 h-4 ${flightStep === 'inbound' ? 'rotate-180' : ''}`} />
                {flightStep === 'outbound' ? 'Select Outbound' : 'Select Return'}
              </div>
              <div className="text-xs opacity-80 mt-1">
                {flightStep === 'outbound' 
                  ? `${searchParams.departureAirport?.code || 'LON'} → ${searchParams.arrivalAirport || 'JED'}`
                  : `${searchParams.arrivalAirport || 'JED'} → ${searchParams.departureAirport?.code || 'LON'}`
                }
              </div>
            </div>
            
            {/* Back to Outbound Button */}
            {flightStep === 'inbound' && (
              <button
                onClick={() => {
                  handleBackToOutbound();
                  setShowMobileFilters(false);
                }}
                className="mx-4 mt-2 w-[calc(100%-2rem)] bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 text-sm flex items-center justify-center gap-2 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Change Outbound Flight
              </button>
            )}
            
            {/* Filter Content */}
            <div className="p-4 space-y-6">
              {/* Results Count */}
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Filter className="w-4 h-4" />
                {filteredFlights.length} Results
              </div>
              
              {/* Stops Filter */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-3 text-sm uppercase">Stops</h4>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: 'all', label: 'All' },
                    { value: 'direct', label: 'Direct' },
                    { value: '1stop', label: '1 Connection' },
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => setFlightFilters(prev => ({ ...prev, stops: option.value }))}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        flightFilters.stops === option.value
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Price Range */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-3 text-sm uppercase">Price Range</h4>
                <div className="flex gap-3 items-center">
                  <input 
                    type="number" 
                    placeholder="Min" 
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    value={flightFilters.priceMin}
                    onChange={(e) => setFlightFilters(prev => ({ ...prev, priceMin: e.target.value }))}
                  />
                  <span className="text-gray-400">-</span>
                  <input 
                    type="number" 
                    placeholder="Max" 
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    value={flightFilters.priceMax}
                    onChange={(e) => setFlightFilters(prev => ({ ...prev, priceMax: e.target.value }))}
                  />
                </div>
              </div>
              
              {/* Departure Time */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-3 text-sm uppercase">Departure Time</h4>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'all', label: 'All Times' },
                    { value: 'morning', label: 'Morning' },
                    { value: 'afternoon', label: 'Afternoon' },
                    { value: 'evening', label: 'Evening' },
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => setFlightFilters(prev => ({ ...prev, departTime: option.value }))}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        flightFilters.departTime === option.value
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Apply Button */}
            <div className="sticky bottom-0 bg-white border-t p-4">
              <Button
                onClick={() => setShowMobileFilters(false)}
                className="w-full h-12 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl"
              >
                Apply Filters
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Mobile Package Modal */}
      {showMobilePackage && (
        <div className="lg:hidden fixed inset-0 z-50">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowMobilePackage(false)}
          />
          
          {/* Modal Content */}
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[85vh] overflow-y-auto animate-slide-up">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
              <h3 className="font-bold text-lg">Your Package</h3>
              <button
                onClick={() => setShowMobilePackage(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Package Content */}
            <div className="p-4 space-y-3">
              {/* Outbound Flight */}
              <div className={`p-3 rounded-lg ${selectedOutboundFlight ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Plane className={`w-4 h-4 ${selectedOutboundFlight ? 'text-green-600' : 'text-gray-400'}`} />
                    <span className="font-medium text-sm">Outbound</span>
                  </div>
                  {selectedOutboundFlight ? (
                    <span className="text-sm font-semibold text-green-600">
                      {formatPrice(selectedOutboundFlight.pricing.total_fare)}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">Not selected</span>
                  )}
                </div>
                {selectedOutboundFlight && (
                  <p className="text-xs text-gray-500 mt-1">
                    {selectedOutboundFlight.validating_carrier} - {selectedOutboundFlight.legs[0]?.departure} → {selectedOutboundFlight.legs[0]?.arrival}
                  </p>
                )}
              </div>
              
              {/* Inbound Flight */}
              <div className={`p-3 rounded-lg ${selectedInboundFlight ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Plane className={`w-4 h-4 rotate-180 ${selectedInboundFlight ? 'text-green-600' : 'text-gray-400'}`} />
                    <span className="font-medium text-sm">Return</span>
                  </div>
                  {selectedInboundFlight ? (
                    <span className="text-sm font-semibold text-green-600">
                      {formatPrice(selectedInboundFlight.pricing.total_fare)}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">Not selected</span>
                  )}
                </div>
                {selectedInboundFlight && (
                  <p className="text-xs text-gray-500 mt-1">
                    {selectedInboundFlight.validating_carrier} - {selectedInboundFlight.legs[0]?.departure} → {selectedInboundFlight.legs[0]?.arrival}
                  </p>
                )}
              </div>
              
              {/* Makkah Hotel */}
              <div className={`p-3 rounded-lg ${selectedMakkahRoom ? 'bg-green-50 border border-green-200' : selectedMakkahHotel ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Building2 className={`w-4 h-4 ${selectedMakkahRoom ? 'text-green-600' : selectedMakkahHotel ? 'text-amber-600' : 'text-gray-400'}`} />
                    <span className="font-medium text-sm">Makkah Hotel</span>
                  </div>
                  {selectedMakkahRoom ? (
                    <span className="text-sm font-semibold text-green-600">
                      {formatPrice(selectedMakkahRoom.total_price)}
                    </span>
                  ) : selectedMakkahHotel ? (
                    <span className="text-xs text-amber-600">Select room</span>
                  ) : (
                    <span className="text-xs text-gray-400">Not selected</span>
                  )}
                </div>
                {selectedMakkahRoom ? (
                  <p className="text-xs text-gray-500 mt-1">{selectedMakkahHotel?.name} • {selectedMakkahRoom.name}</p>
                ) : selectedMakkahHotel && (
                  <p className="text-xs text-amber-600 mt-1">{selectedMakkahHotel.name}</p>
                )}
              </div>
              
              {/* Madinah Hotel */}
              <div className={`p-3 rounded-lg ${selectedMadinahRoom ? 'bg-green-50 border border-green-200' : selectedMadinahHotel ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Building2 className={`w-4 h-4 ${selectedMadinahRoom ? 'text-green-600' : selectedMadinahHotel ? 'text-amber-600' : 'text-gray-400'}`} />
                    <span className="font-medium text-sm">Madinah Hotel</span>
                  </div>
                  {selectedMadinahRoom ? (
                    <span className="text-sm font-semibold text-green-600">
                      {formatPrice(selectedMadinahRoom.total_price)}
                    </span>
                  ) : selectedMadinahHotel ? (
                    <span className="text-xs text-amber-600">Select room</span>
                  ) : (
                    <span className="text-xs text-gray-400">Not selected</span>
                  )}
                </div>
                {selectedMadinahRoom ? (
                  <p className="text-xs text-gray-500 mt-1">{selectedMadinahHotel?.name} • {selectedMadinahRoom.name}</p>
                ) : selectedMadinahHotel && (
                  <p className="text-xs text-amber-600 mt-1">{selectedMadinahHotel.name}</p>
                )}
              </div>
              
              {/* Transport */}
              <div className={`p-3 rounded-lg ${selectedTransport ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Bus className={`w-4 h-4 ${selectedTransport ? 'text-green-600' : 'text-gray-400'}`} />
                    <span className="font-medium text-sm">Transport</span>
                  </div>
                  {selectedTransport ? (
                    <span className="text-sm font-semibold text-green-600">
                      {formatPrice(selectedTransport.standard_package_price || selectedTransport.price || 0)}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">Optional</span>
                  )}
                </div>
              </div>
              
              {/* Visa */}
              <div className={`p-3 rounded-lg ${selectedVisa ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className={`w-4 h-4 ${selectedVisa ? 'text-green-600' : 'text-gray-400'}`} />
                    <span className="font-medium text-sm">Visa</span>
                  </div>
                  {selectedVisa ? (
                    <span className="text-sm font-semibold text-green-600">
                      {formatPrice(selectedVisa.price)}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">Optional</span>
                  )}
                </div>
                {selectedVisa && (
                  <p className="text-xs text-gray-500 mt-1">{selectedVisa.name}</p>
                )}
              </div>
            </div>
            
            {/* Total */}
            <div className="px-4 pb-4 pt-2 border-t mx-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-semibold">{formatPrice(calculateTotalPrice())}</span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-600">Service Fee (5%)</span>
                <span className="font-semibold">{formatPrice(calculateTotalPrice() * 0.05)}</span>
              </div>
              <div className="flex items-center justify-between text-lg font-bold">
                <span>Total</span>
                <span className="text-green-600">{formatPrice(calculateTotalPrice() * 1.05)}</span>
              </div>
              <div className="text-xs text-gray-500 mt-1 text-right">
                Deposit: {formatPrice(50 * (searchParams.adults + searchParams.children + (searchParams.infants || 0)))} (£50/person)
              </div>
            </div>
            
            {/* Continue Button */}
            <div className="sticky bottom-0 bg-white border-t p-4">
              <Button
                onClick={() => {
                  setShowMobilePackage(false);
                  handleProceedToBooking();
                }}
                disabled={!canProceedToBooking}
                className="w-full h-12 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold rounded-xl disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <ShoppingCart className="w-5 h-5" />
                Continue to Booking
              </Button>
              {!canProceedToBooking && (
                <p className="text-xs text-gray-500 text-center mt-2">
                  Select flights to continue
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Flight Timeline Component - moved outside FlightCard for performance
const FlightTimeline = ({ duration, hasLayover, layoverTime, layoverCode }) => (
  <div className="flex-1 px-4">
    <div className="text-center text-xs text-gray-500 mb-1">{duration}</div>
    <div className="relative flex items-center">
      <div className="w-2 h-2 bg-blue-500 rounded-full z-10"></div>
      <div className="flex-1 h-0.5 bg-gray-300 relative">
        {hasLayover && (
          <>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-orange-400 rounded-full z-10"></div>
            <div className="absolute top-3 left-1/2 -translate-x-1/2 whitespace-nowrap">
              <span className="text-[10px] text-orange-500 font-medium">{layoverTime} in {layoverCode}</span>
            </div>
          </>
        )}
      </div>
      <div className="w-2 h-2 bg-blue-500 rounded-full z-10"></div>
    </div>
  </div>
);

// Flight Card Component
const FlightCard = ({ 
  flight, 
  selected, 
  onSelect, 
  formatPrice, 
  formatTime, 
  formatDate, 
  priceDisplay, 
  passengers,
  isDirect,
  flightType
}) => {
  const [selectedBaggage, setSelectedBaggage] = useState('included');
  const [showBaggageOptions, setShowBaggageOptions] = useState(false);
  
  // Get baggage info from API data
  const apiBaggage = flight.baggage || {};
  const checkedBags = apiBaggage.checked_bags || 1;
  const weightPerBag = apiBaggage.weight_per_bag || '23kg';
  const cabinBag = apiBaggage.cabin_bag || '7kg';
  
  // Build baggage options based on API data
  const baggageOptions = [
    { id: 'none', label: 'No Hold Luggage', weight: '0 kg', price: 0, description: `Cabin bag only (${cabinBag})` },
    { id: 'included', label: `${checkedBags}x Hold Luggage`, weight: weightPerBag, price: 0, description: `Included in fare` },
    { id: 'extra1', label: `${checkedBags + 1}x Hold Luggage`, weight: `${parseInt(weightPerBag) * (checkedBags + 1)} kg`, price: 45, description: 'Add extra bag' },
    { id: 'extra2', label: `${checkedBags + 2}x Hold Luggage`, weight: `${parseInt(weightPerBag) * (checkedBags + 2)} kg`, price: 90, description: 'Add 2 extra bags' },
  ];
  
  const currentBaggage = baggageOptions.find(b => b.id === selectedBaggage) || baggageOptions[1];
  
  const airlineColors = {
    'SV': { bg: 'bg-green-600', header: 'bg-gradient-to-r from-green-500 to-green-600', text: 'text-green-700' },
    'EK': { bg: 'bg-red-600', header: 'bg-gradient-to-r from-red-500 to-red-600', text: 'text-red-700' },
    'QR': { bg: 'bg-purple-600', header: 'bg-gradient-to-r from-purple-500 to-purple-600', text: 'text-purple-700' },
    'TK': { bg: 'bg-red-700', header: 'bg-gradient-to-r from-red-600 to-red-700', text: 'text-red-700' },
  };
  
  const carrier = flight.validating_carrier || 'SV';
  const colors = airlineColors[carrier] || { bg: 'bg-gray-500', header: 'bg-gradient-to-r from-gray-500 to-gray-600', text: 'text-gray-700' };
  const airlineName = flight.legs[0]?.segments[0]?.airline_name || 'Airline';
  const flightNumber = flight.legs[0]?.segments[0]?.flight_number || '';
  const stops = flight.legs[0]?.stops || 0;
  
  // Layover info
  const layoverAirport = 'DOH';
  const layoverDuration = stops > 0 ? '5h 0m' : null;
  
  const basePrice = priceDisplay === 'perPerson' 
    ? flight.pricing.per_adult 
    : flight.pricing.total_fare * passengers;
  
  const baggagePrice = currentBaggage.price * passengers;
  const displayPrice = basePrice + baggagePrice;

  return (
    <div 
      className={`bg-white rounded-xl shadow-sm overflow-hidden border-2 transition-all cursor-pointer ${
        selected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-transparent hover:shadow-md'
      }`}
      onClick={onSelect}
    >
      {/* Airline Header */}
      <div className={`${colors.header} text-white px-4 py-2.5 flex items-center justify-between`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
            <span className={`font-bold text-sm ${colors.text}`}>{carrier}</span>
          </div>
          <div>
            <div className="font-semibold">{airlineName}</div>
            <div className="text-xs opacity-80">{flightNumber}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${flightType === 'outbound' ? 'bg-blue-400/50' : 'bg-green-400/50'}`}>
            {flightType === 'outbound' ? 'Outbound' : 'Return'}
          </span>
          <span className={`px-3 py-1 rounded-lg text-xs font-medium ${isDirect ? 'bg-white/20' : 'bg-orange-500'}`}>
            {isDirect ? 'Direct' : `${stops} Stop`}
          </span>
        </div>
      </div>
      
      {/* Flight Details */}
      <div className="p-4">
        <div className="flex items-center mb-4">
          {/* Departure */}
          <div className="text-left">
            <div className="text-2xl font-bold text-gray-800">{formatTime(flight.legs[0]?.departure_time)}</div>
            <div className="text-xs text-gray-500">{formatDate(flight.legs[0]?.departure_time)}</div>
            <div className="mt-1 inline-block px-2 py-0.5 bg-blue-500 text-white text-xs rounded font-medium">
              {flight.legs[0]?.departure}
            </div>
          </div>
          
          {/* Timeline */}
          <FlightTimeline 
            duration={flight.legs[0]?.duration} 
            hasLayover={!isDirect} 
            layoverTime={layoverDuration}
            layoverCode={layoverAirport}
          />
          
          {/* Arrival */}
          <div className="text-right">
            <div className="text-2xl font-bold text-gray-800">{formatTime(flight.legs[0]?.arrival_time)}</div>
            <div className="text-xs text-gray-500">{formatDate(flight.legs[0]?.arrival_time)}</div>
            <div className="mt-1 inline-block px-2 py-0.5 bg-gray-700 text-white text-xs rounded font-medium">
              {flight.legs[0]?.arrival}
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded font-medium">Economy</span>
            
            {/* Baggage Dropdown */}
            <div className="relative z-50">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowBaggageOptions(!showBaggageOptions);
                }}
                className="flex items-center gap-2 px-2 py-1 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <Luggage className="w-3.5 h-3.5 text-blue-600" />
                <span className="text-xs font-medium text-blue-700">{currentBaggage.weight}</span>
                <ChevronDown className={`w-3.5 h-3.5 text-blue-600 transition-transform ${showBaggageOptions ? 'rotate-180' : ''}`} />
              </button>
              
              {showBaggageOptions && (
                <>
                  <div 
                    className="fixed inset-0 z-40"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowBaggageOptions(false);
                    }}
                  />
                  <div 
                    className="absolute bottom-full left-0 mb-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="p-3 bg-blue-500 text-white">
                      <div className="font-semibold">Select Baggage Allowance</div>
                      <div className="text-xs opacity-80 mt-1">Cabin bag ({cabinBag}) included with all options</div>
                    </div>
                    {baggageOptions.map((option) => (
                      <button
                        key={option.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedBaggage(option.id);
                          setShowBaggageOptions(false);
                        }}
                        className={`w-full px-4 py-3 flex items-center justify-between hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                          selectedBaggage === option.id ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Luggage className={`w-5 h-5 ${option.id === 'none' ? 'text-gray-400' : 'text-blue-600'}`} />
                          <div className="text-left">
                            <div className="text-sm font-medium text-gray-800">{option.label}</div>
                            <div className="text-xs text-gray-500">{option.description}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {option.price > 0 ? (
                            <span className="text-sm font-bold text-blue-600">+£{option.price}</span>
                          ) : (
                            <span className="text-xs text-green-600 font-semibold bg-green-100 px-2 py-0.5 rounded">
                              {option.id === 'none' ? 'Free' : 'Included'}
                            </span>
                          )}
                          {selectedBaggage === option.id && (
                            <Check className="w-5 h-5 text-blue-600" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            
            {currentBaggage.price > 0 && (
              <span className="text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                +£{currentBaggage.price * passengers}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-xl font-bold text-gray-800">{formatPrice(displayPrice)}</div>
              <div className="text-xs text-gray-500">
                {priceDisplay === 'perPerson' ? 'per person' : `for ${passengers} passengers`}
              </div>
            </div>
            <button 
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                selected 
                  ? 'bg-green-500 text-white' 
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              {selected ? 'Selected ✓' : 'Select'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Hotel Section Component with Room Type Selection
const HotelSection = ({
  hotels,
  selectedHotel,
  selectedRoom,
  onSelectHotel,
  onSelectRoom,
  onBackToHotels,
  formatPrice,
  hotelType,
  nights,
  guests
}) => {
  // Generate room types for a hotel
  const generateRoomTypes = (hotel) => {
    const basePrice = hotel.price_per_night;
    return [
      {
        id: `${hotel.id}-standard`,
        name: 'Standard Room',
        description: 'Comfortable room with city view',
        bedType: '1 King Bed or 2 Single Beds',
        maxGuests: 2,
        size: '25 m²',
        amenities: ['Free WiFi', 'Air Conditioning', 'TV', 'Safe', 'Mini Fridge'],
        price_per_night: basePrice,
        total_price: basePrice * nights,
        available: 5,
        refundable: true,
        breakfast: false
      },
      {
        id: `${hotel.id}-superior`,
        name: 'Superior Room',
        description: 'Spacious room with partial Haram view',
        bedType: '1 King Bed',
        maxGuests: 3,
        size: '32 m²',
        amenities: ['Free WiFi', 'Air Conditioning', 'TV', 'Safe', 'Mini Bar', 'Bathrobe'],
        price_per_night: Math.round(basePrice * 1.3),
        total_price: Math.round(basePrice * 1.3) * nights,
        available: 3,
        refundable: true,
        breakfast: false
      },
      {
        id: `${hotel.id}-deluxe`,
        name: 'Deluxe Room',
        description: 'Premium room with Haram view',
        bedType: '1 King Bed or 2 Queen Beds',
        maxGuests: 4,
        size: '40 m²',
        amenities: ['Free WiFi', 'Air Conditioning', 'TV', 'Safe', 'Mini Bar', 'Bathrobe', 'Nespresso Machine'],
        price_per_night: Math.round(basePrice * 1.6),
        total_price: Math.round(basePrice * 1.6) * nights,
        available: 2,
        refundable: true,
        breakfast: true
      },
      {
        id: `${hotel.id}-suite`,
        name: 'Executive Suite',
        description: 'Luxury suite with direct Haram view & living area',
        bedType: '1 King Bed + Sofa Bed',
        maxGuests: 4,
        size: '55 m²',
        amenities: ['Free WiFi', 'Air Conditioning', 'TV', 'Safe', 'Mini Bar', 'Bathrobe', 'Nespresso Machine', 'Living Room', 'Butler Service'],
        price_per_night: Math.round(basePrice * 2.2),
        total_price: Math.round(basePrice * 2.2) * nights,
        available: 1,
        refundable: false,
        breakfast: true
      },
      {
        id: `${hotel.id}-family`,
        name: 'Family Room',
        description: 'Large room ideal for families',
        bedType: '2 Queen Beds',
        maxGuests: 5,
        size: '45 m²',
        amenities: ['Free WiFi', 'Air Conditioning', 'TV', 'Safe', 'Mini Fridge', 'Extra Towels', 'Connecting Door Available'],
        price_per_night: Math.round(basePrice * 1.8),
        total_price: Math.round(basePrice * 1.8) * nights,
        available: 2,
        refundable: true,
        breakfast: true
      }
    ];
  };

  // If a hotel is selected, show room types
  if (selectedHotel && !selectedRoom) {
    const roomTypes = generateRoomTypes(selectedHotel);
    
    return (
      <div>
        {/* Selected Hotel Summary */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src={selectedHotel.image} 
                alt={selectedHotel.name}
                className="w-16 h-16 rounded-lg object-cover"
              />
              <div>
                <div className="text-sm font-semibold text-amber-800">{selectedHotel.name}</div>
                <div className="flex items-center gap-1 mt-1">
                  {[...Array(selectedHotel.stars)].map((_, i) => (
                    <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />
                  ))}
                  <span className="text-xs text-amber-600 ml-2">{selectedHotel.distance_to_haram}</span>
                </div>
                <div className="text-xs text-amber-600 mt-1">{nights} nights • {guests} guests</div>
              </div>
            </div>
            <button 
              onClick={onBackToHotels}
              className="text-sm text-amber-700 hover:text-amber-900 flex items-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" />
              Change Hotel
            </button>
          </div>
        </div>
        
        {/* Room Types Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <h3 className="text-lg font-bold text-gray-800">Select Room Type</h3>
          <p className="text-sm text-gray-500 mt-1">Choose from {roomTypes.length} available room types</p>
        </div>
        
        {/* Room Types List */}
        <div className="space-y-4">
          {roomTypes.map(room => (
            <div
              key={room.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-all"
            >
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-gray-800">{room.name}</h4>
                      {room.breakfast && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                          Breakfast Included
                        </span>
                      )}
                      {room.available <= 2 && (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                          Only {room.available} left!
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{room.description}</p>
                    
                    <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        <span>Max {room.maxGuests} guests</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span>🛏️ {room.bedType}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span>📐 {room.size}</span>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 mt-3">
                      {room.amenities.slice(0, 5).map((amenity, i) => (
                        <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                          {amenity}
                        </span>
                      ))}
                      {room.amenities.length > 5 && (
                        <span className="text-xs text-blue-600">+{room.amenities.length - 5} more</span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-3 mt-3">
                      {room.refundable ? (
                        <span className="text-xs text-green-600 flex items-center gap-1">
                          <Check className="w-3 h-3" /> Free cancellation
                        </span>
                      ) : (
                        <span className="text-xs text-orange-600 flex items-center gap-1">
                          <X className="w-3 h-3" /> Non-refundable
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-right ml-4">
                    <div className="text-sm text-gray-500">{nights} nights</div>
                    <div className="text-2xl font-bold text-gray-800">
                      {formatPrice(room.total_price)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {formatPrice(room.price_per_night)}/night
                    </div>
                    <button
                      onClick={() => onSelectRoom(room)}
                      className="mt-3 px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-full text-sm font-semibold transition-colors"
                    >
                      Select Room
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Show hotels list - Updated to match reference design
  return (
    <div className="space-y-4">
      {hotels.map(hotel => {
        // Generate a random rating for demo (in production, this would come from API)
        const rating = hotel.rating || (7 + Math.random() * 2).toFixed(1);
        const reviewCount = hotel.reviewCount || Math.floor(500 + Math.random() * 3000);
        const ratingLabel = rating >= 9 ? 'Exceptional' : rating >= 8 ? 'Excellent' : rating >= 7 ? 'Very Good' : 'Good';
        
        return (
          <div
            key={hotel.id}
            className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all"
          >
            <div className="flex">
              {/* Hotel Image */}
              <div className="w-72 h-52 bg-gray-200 flex-shrink-0 relative">
                <img 
                  src={hotel.image} 
                  alt={hotel.name}
                  className="w-full h-full object-cover"
                />
                {/* Distance Badge */}
                <div className="absolute top-3 left-3 bg-[#1e3a5f] text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {hotel.distance_to_haram}
                </div>
              </div>
              
              {/* Hotel Details */}
              <div className="flex-1 p-5">
                <div className="flex items-start justify-between h-full">
                  {/* Left Section - Hotel Info */}
                  <div className="flex-1">
                    {/* Hotel Name & Stars */}
                    <div className="flex items-center gap-3">
                      <h3 className="font-bold text-xl text-gray-900">{hotel.name}</h3>
                      <div className="flex">
                        {[...Array(hotel.stars || 5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-[#d4af37] text-[#d4af37]" />
                        ))}
                      </div>
                    </div>
                    
                    {/* Rating */}
                    <div className="flex items-center gap-2 mt-2">
                      <div className="bg-[#1e3a5f] text-white px-2.5 py-1 rounded-lg font-bold text-sm">
                        {rating}
                      </div>
                      <div className="text-sm">
                        <span className="font-semibold text-gray-800">{ratingLabel}</span>
                        <span className="text-gray-500 ml-1">({reviewCount.toLocaleString()} reviews)</span>
                      </div>
                    </div>
                    
                    {/* Amenities */}
                    <div className="flex flex-wrap gap-2 mt-4">
                      {hotel.amenities?.slice(0, 5).map((amenity, i) => (
                        <span key={i} className="text-xs bg-gray-100 text-gray-700 px-3 py-1.5 rounded-full">
                          {amenity}
                        </span>
                      ))}
                      {hotel.refundable && (
                        <span className="text-xs bg-green-100 text-green-700 px-3 py-1.5 rounded-full font-medium">
                          Free Cancellation
                        </span>
                      )}
                    </div>
                    
                    {/* Room Info */}
                    <div className="mt-4 text-sm text-gray-600">
                      <span className="font-medium">{nights} nights</span>
                      <span className="mx-2">•</span>
                      <span>{guests} guests</span>
                    </div>
                  </div>
                  
                  {/* Right Section - Price & Select */}
                  <div className="text-right flex flex-col justify-between h-full ml-6 min-w-[140px]">
                    <div>
                      <div className="text-sm text-gray-500">From</div>
                      <div className="text-3xl font-bold text-[#1e3a5f]">
                        {formatPrice(hotel.price_per_night)}
                      </div>
                      <div className="text-sm text-gray-500">per night</div>
                    </div>
                    
                    <button 
                      onClick={() => onSelectHotel(hotel)}
                      className="mt-4 w-full px-6 py-3 bg-[#d4af37] hover:bg-[#c9a432] text-[#1e3a5f] rounded-lg text-sm font-bold transition-colors"
                    >
                      Select
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Transport & Ziyarat Section Component
const TransportZiyaratSection = ({
  transportOptions,
  selectedVehicle,
  setSelectedVehicle,
  selectedTransfers,
  setSelectedTransfers,
  selectedZiyarat,
  setSelectedZiyarat,
  passengers,
  formatPrice,
  getRecommendedVehicle,
  onBack
}) => {
  // Vehicle definitions with icons
  const vehicleIcons = {
    car: (
      <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M5 17h14v-5l-2-4H7l-2 4v5zM5 17v2m14-2v2m-3-9V7M8 8V7m9 10h-2m-6 0H7" />
        <circle cx="7" cy="17" r="1" />
        <circle cx="17" cy="17" r="1" />
      </svg>
    ),
    h1van: (
      <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="2" y="8" width="20" height="10" rx="2" />
        <path d="M6 8V6a2 2 0 012-2h8a2 2 0 012 2v2M6 18v1m12-1v1" />
        <circle cx="6" cy="18" r="1" />
        <circle cx="18" cy="18" r="1" />
      </svg>
    ),
    van: (
      <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="2" y="8" width="20" height="10" rx="2" />
        <path d="M6 8V6a2 2 0 012-2h8a2 2 0 012 2v2M6 18v1m12-1v1" />
        <circle cx="6" cy="18" r="1" />
        <circle cx="18" cy="18" r="1" />
      </svg>
    ),
    hiace: (
      <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="2" y="7" width="20" height="11" rx="2" />
        <path d="M6 7V5a2 2 0 012-2h8a2 2 0 012 2v2M5 18v1m14-1v1" />
        <circle cx="6" cy="18" r="1.5" />
        <circle cx="18" cy="18" r="1.5" />
        <line x1="9" y1="11" x2="9" y2="14" />
        <line x1="15" y1="11" x2="15" y2="14" />
      </svg>
    ),
    minibus: (
      <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="2" y="7" width="20" height="11" rx="2" />
        <path d="M6 7V5a2 2 0 012-2h8a2 2 0 012 2v2M5 18v1m14-1v1" />
        <circle cx="6" cy="18" r="1.5" />
        <circle cx="18" cy="18" r="1.5" />
        <line x1="9" y1="11" x2="9" y2="14" />
        <line x1="15" y1="11" x2="15" y2="14" />
      </svg>
    ),
    gmc: (
      <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="2" y="6" width="20" height="12" rx="2" />
        <path d="M4 6V4m16 2V4M4 18v1m16-1v1" />
        <circle cx="6" cy="18" r="2" />
        <circle cx="18" cy="18" r="2" />
        <path d="M6 9h12M6 12h12" />
      </svg>
    ),
    suv: (
      <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="2" y="6" width="20" height="12" rx="2" />
        <path d="M4 6V4m16 2V4M4 18v1m16-1v1" />
        <circle cx="6" cy="18" r="2" />
        <circle cx="18" cy="18" r="2" />
        <path d="M6 9h12M6 12h12" />
      </svg>
    ),
    coaster: (
      <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="2" y="6" width="20" height="12" rx="2" />
        <path d="M4 6V4m16 2V4M4 18v1m16-1v1" />
        <circle cx="6" cy="18" r="2" />
        <circle cx="18" cy="18" r="2" />
        <line x1="7" y1="10" x2="7" y2="13" />
        <line x1="11" y1="10" x2="11" y2="13" />
        <line x1="15" y1="10" x2="15" y2="13" />
      </svg>
    ),
    bus: (
      <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="3" y="4" width="18" height="14" rx="2" />
        <path d="M3 10h18M6 18v2m12-2v2" />
        <circle cx="7" cy="18" r="2" />
        <circle cx="17" cy="18" r="2" />
        <line x1="8" y1="7" x2="8" y2="9" />
        <line x1="12" y1="7" x2="12" y2="9" />
        <line x1="16" y1="7" x2="16" y2="9" />
      </svg>
    )
  };

  // Transfer routes with route IDs matching backend
  const transferRoutes = [
    { id: 'jeddah_makkah', name: 'Jeddah Airport → Makkah Hotel', icon: '✈️' },
    { id: 'makkah_madinah', name: 'Makkah → Madinah Hotel', icon: '🕋' },
    { id: 'madinah_jeddah', name: 'Madinah → Jeddah Airport', icon: '🕌' },
    { id: 'madinah_airport', name: 'Madinah Hotel → Madinah Airport', icon: '🛫' },
  ];

  // Get route price based on selected vehicle
  const getRoutePrice = (routeId) => {
    if (!selectedVehicle || !selectedVehicle.route_prices) return 0;
    return selectedVehicle.route_prices[routeId] || 0;
  };

  // Ziyarat tours with route IDs matching backend
  const ziyaratTours = [
    { 
      id: 'makkah_ziyarat', 
      name: 'Makkah Ziyarat Tour', 
      location: 'Makkah',
      description: 'Visit Cave of Hira, Cave of Thawr, Arafat, Muzdalifah, Mina',
      duration: '6 hours',
    },
    { 
      id: 'madinah_ziyarat', 
      name: 'Medina Ziyarat Tour', 
      location: 'Medina',
      description: 'Visit Quba Mosque, Uhud Mountain, Qiblatain Mosque, Date Farms',
      duration: '5 hours',
    }
  ];

  const toggleTransfer = (transferId) => {
    if (selectedTransfers.includes(transferId)) {
      setSelectedTransfers(selectedTransfers.filter(id => id !== transferId));
    } else {
      setSelectedTransfers([...selectedTransfers, transferId]);
    }
  };

  const toggleZiyarat = (tourId) => {
    if (selectedZiyarat.includes(tourId)) {
      setSelectedZiyarat(selectedZiyarat.filter(id => id !== tourId));
    } else {
      setSelectedZiyarat([...selectedZiyarat, tourId]);
    }
  };

  // Get recommended vehicle
  const recommendedVehicle = getRecommendedVehicle(passengers, transportOptions);

  // Calculate transport cost using route-based pricing
  const calculateTransportCost = () => {
    let total = 0;
    if (selectedVehicle && selectedVehicle.route_prices) {
      // Add up prices for selected transfer routes
      selectedTransfers.forEach(routeId => {
        total += selectedVehicle.route_prices[routeId] || 0;
      });
      // Add Ziyarat tour prices
      selectedZiyarat.forEach(tourId => {
        total += selectedVehicle.route_prices[tourId] || 0;
      });
    }
    return total;
  };

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="bg-gradient-to-r from-purple-600 via-purple-500 to-pink-500 rounded-t-xl p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Bus className="w-6 h-6 text-white" />
          <div>
            <h2 className="text-xl font-bold text-white">Transport & Ziyarat</h2>
            <p className="text-purple-100 text-sm">Choose your transportation and religious tours</p>
          </div>
        </div>
        <button 
          onClick={onBack}
          className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg font-medium transition-colors"
        >
          ← Back
        </button>
      </div>

      {/* Vehicle Selection Section */}
      <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Bus className="w-5 h-5 text-white" />
            <h3 className="font-bold text-white text-lg">Select Vehicle Type</h3>
          </div>
          <div className="bg-white/20 px-3 py-1 rounded-full text-white text-sm">
            {passengers} passengers → {recommendedVehicle?.name || 'Select'}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {transportOptions.map(vehicle => {
            const isRecommended = vehicle.is_recommended;
            const isSelected = selectedVehicle?.id === vehicle.id;
            
            return (
              <div
                key={vehicle.id}
                data-testid={`vehicle-${vehicle.vehicle_type}`}
                onClick={() => setSelectedVehicle(vehicle)}
                className={`relative bg-white rounded-xl p-4 cursor-pointer transition-all flex flex-col items-center ${
                  isSelected 
                    ? 'ring-2 ring-purple-500 shadow-lg transform scale-105' 
                    : 'hover:shadow-md hover:scale-102'
                }`}
              >
                {isRecommended && (
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                    ✓ Recommended
                  </div>
                )}
                
                <div className={`w-16 h-16 rounded-lg flex items-center justify-center mb-2 ${
                  isSelected ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-500'
                }`}>
                  {vehicleIcons[vehicle.vehicle_type] || vehicleIcons[vehicle.icon] || vehicleIcons.car}
                </div>
                
                <h4 className="font-semibold text-gray-800 text-center">{vehicle.name}</h4>
                <p className="text-xs text-gray-500 text-center mt-1">
                  {vehicle.min_passengers}-{vehicle.max_passengers} passengers
                </p>
                
                {/* Show standard package price */}
                {vehicle.standard_package_price > 0 && (
                  <p className="text-sm font-bold text-purple-600 mt-1">
                    {formatPrice(vehicle.standard_package_price)}
                  </p>
                )}
                
                {isSelected && (
                  <div className="mt-2 flex items-center gap-1 text-purple-600 text-sm font-medium">
                    <Check className="w-4 h-4" /> Selected
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Transfer Routes Section */}
      <div className="bg-gradient-to-r from-pink-500 to-rose-500 rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
            </svg>
            <h3 className="font-bold text-white text-lg">Transfer Routes</h3>
          </div>
          <span className="bg-white/20 px-3 py-1 rounded-full text-white text-sm">Optional</span>
        </div>
        <p className="text-pink-100 text-sm mb-4">Select the transfers you need (costs included in total)</p>

        <div className="space-y-3">
          {transferRoutes.map(route => {
            const isSelected = selectedTransfers.includes(route.id);
            const routePrice = getRoutePrice(route.id);
            return (
              <div
                key={route.id}
                data-testid={`transfer-${route.id}`}
                onClick={() => toggleTransfer(route.id)}
                className={`bg-white rounded-xl p-4 cursor-pointer transition-all flex items-center justify-between ${
                  isSelected ? 'ring-2 ring-pink-500' : 'hover:shadow-md'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    isSelected ? 'bg-pink-100' : 'bg-gray-100'
                  }`}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      className="w-5 h-5 text-pink-500 rounded cursor-pointer"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{route.icon}</span>
                    <span className="font-medium text-gray-800">{route.name}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {routePrice > 0 && (
                    <span className="text-pink-600 font-semibold">{formatPrice(routePrice)}</span>
                  )}
                  {isSelected && (
                    <span className="text-pink-500 font-medium text-sm">✓</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Optional Ziyarat Tours Section */}
      <div className="bg-gradient-to-r from-teal-500 to-emerald-500 rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5 text-white" />
            <h3 className="font-bold text-white text-lg">Optional Ziyarat Tours</h3>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {ziyaratTours.map(tour => {
            const isSelected = selectedZiyarat.includes(tour.id);
            const tourPrice = getRoutePrice(tour.id);
            return (
              <div
                key={tour.id}
                data-testid={`ziyarat-${tour.id}`}
                onClick={() => toggleZiyarat(tour.id)}
                className={`bg-white rounded-xl p-4 cursor-pointer transition-all ${
                  isSelected ? 'ring-2 ring-teal-500' : 'hover:shadow-md'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-gray-800">{tour.name}</h4>
                      <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">
                        {tour.location}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">{tour.description}</p>
                    <div className="flex items-center gap-4 mt-2">
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {tour.duration}
                      </span>
                      {tourPrice > 0 && (
                        <span className="text-sm font-bold text-teal-600">
                          {formatPrice(tourPrice)}/vehicle
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      isSelected 
                        ? 'border-teal-500 bg-teal-500' 
                        : 'border-gray-300'
                    }`}>
                      {isSelected && <Check className="w-4 h-4 text-white" />}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-teal-100 text-sm mt-3 italic">
          {selectedZiyarat.length === 0 
            ? 'No Ziyarat tours selected (optional)'
            : `${selectedZiyarat.length} tour(s) selected`
          }
        </p>
      </div>

      {/* Summary */}
      <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl p-4 border border-purple-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Check className="w-5 h-5 text-purple-600" />
            <span className="font-medium text-gray-700">Transport & Ziyarat costs included in total package price</span>
          </div>
          <div className="text-right">
            <span className="text-sm text-gray-500">
              {selectedTransfers.length} of 3 transfers + {selectedZiyarat.length} Ziyarat tours selected
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchResults;
